// Shell-level Messenger host — centered expanded messenger-only overlay.
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { WorkplaceMessengerBody } from "@/components/messages/WorkplaceMessengerBody";
import { useSidebarBreakpoint } from "@/components/shell/Sidebar/useSidebarBreakpoint";
import { openFireflyActivityDock } from "@/components/activity/activityDockOpenEvent";
import {
  MessengerDragExpandHandle,
  useMessengerContractedResize,
} from "@/components/shell/MessengerDragExpandHandle";
import {
  MESSENGER_CLOSE_EVENT,
  MESSENGER_OPEN_EVENT,
  MESSENGER_TOGGLE_EVENT,
  publishMessengerState,
  readMessengerOpenSession,
  type MessengerOpenDetail,
} from "@/components/shell/popovers/messengerOpenEvent";
import {
  getMessengerSizeMode,
  setMessengerSizeMode,
  subscribeMessengerSizeMode,
  type MessengerSizeMode,
} from "@/lib/messages/messengerSizePreference";
import { messengerNearFireflyStyle } from "@/lib/messages/messengerNearFireflyPlacement";

export function MessengerShellHost() {
  const variant = useSidebarBreakpoint();
  const isMobile = variant === "mobile";
  const [open, setOpen] = useState(() => readMessengerOpenSession());
  const [sizeMode, setSizeMode] = useState<MessengerSizeMode>(() => getMessengerSizeMode());
  const [placementTick, setPlacementTick] = useState(0);
  const contracted = !isMobile && sizeMode === "contracted";
  const resizeEnabled = open && contracted;
  const { size: panelSize, setSize: setPanelSize, handleProps, pinchPanelRef } =
    useMessengerContractedResize(resizeEnabled, {
      onSnapExpand: () => {
        if (!isMobile) setMessengerSizeMode("expanded");
      },
    });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onOpen = (e: Event) => {
      const detail = (e as CustomEvent<MessengerOpenDetail>).detail;
      if (detail && detail.force === false) return;
      setOpen(true);
    };
    const onClose = () => {
      setOpen(false);
      void import("@/lib/firefly/fireflySidebarDockTravel").then((m) => {
        if (m.isFireflySidebarDockTravelActive()) {
          m.setFireflySidebarDockTravelActive(false);
        }
      });
    };
    const onToggle = () =>
      setOpen((v) => {
        if (v) {
          void import("@/lib/firefly/fireflySidebarDockTravel").then((m) => {
            if (m.isFireflySidebarDockTravelActive()) {
              m.setFireflySidebarDockTravelActive(false);
            }
          });
        }
        return !v;
      });
    window.addEventListener(MESSENGER_OPEN_EVENT, onOpen);
    window.addEventListener(MESSENGER_CLOSE_EVENT, onClose);
    window.addEventListener(MESSENGER_TOGGLE_EVENT, onToggle);
    return () => {
      window.removeEventListener(MESSENGER_OPEN_EVENT, onOpen);
      window.removeEventListener(MESSENGER_CLOSE_EVENT, onClose);
      window.removeEventListener(MESSENGER_TOGGLE_EVENT, onToggle);
    };
  }, []);

  useEffect(() => subscribeMessengerSizeMode(setSizeMode), []);

  useEffect(() => {
    publishMessengerState(open);
  }, [open]);

  // Recompute near-Firefly placement on open / resize.
  useEffect(() => {
    if (!open || !contracted) return;
    const bump = () => setPlacementTick((n) => n + 1);
    bump();
    window.addEventListener("resize", bump);
    return () => window.removeEventListener("resize", bump);
  }, [open, contracted]);

  // Mobile: true viewport fill (edge-to-edge). Pinch resize disabled on mobile fullscreen.
  useEffect(() => {
    if (!open || !isMobile || typeof window === "undefined") return;
    setPanelSize({
      width: window.innerWidth,
      height: window.innerHeight,
    });
  }, [open, isMobile, setPanelSize]);

  function clearStuckFireflyDockTravel() {
    void import("@/lib/firefly/fireflySidebarDockTravel").then((m) => {
      if (m.isFireflySidebarDockTravelActive()) {
        m.setFireflySidebarDockTravelActive(false);
      }
    });
  }

  function closeMessenger() {
    setOpen(false);
    clearStuckFireflyDockTravel();
  }

  function returnToFireflyMessenger() {
    setMessengerSizeMode("contracted");
    closeMessenger();
    void openFireflyActivityDock({ force: true, focusSection: "messages" });
  }

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeMessenger();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const contractedPlacement = useMemo(() => {
    if (!contracted) return undefined;
    return messengerNearFireflyStyle(panelSize);
  }, [contracted, panelSize.width, panelSize.height, placementTick]);

  if (!open) return null;

  const layout = isMobile ? "fullscreen" : sizeMode === "expanded" ? "expanded" : "contracted";
  const expandedDesktop = !isMobile && sizeMode === "expanded";
  const mobileSize = isMobile
    ? {
        width: "100vw",
        height: "100dvh",
        paddingBottom: "env(safe-area-inset-bottom)",
        paddingTop: "env(safe-area-inset-top)",
      }
    : undefined;

  const panel = (
    <div
      ref={resizeEnabled ? pinchPanelRef : undefined}
      role="dialog"
      aria-modal="true"
      aria-label="Messenger"
      className={
        isMobile
          ? "relative flex h-full w-full flex-col overflow-hidden bg-white text-gray-900 dark:bg-zinc-950 dark:text-gray-100"
          : expandedDesktop
            ? "relative flex h-[min(85vh,820px)] w-[min(1100px,calc(100vw-2rem))] flex-col overflow-hidden rounded-2xl border border-teal-200 bg-white text-gray-900 shadow-2xl dark:border-teal-900 dark:bg-zinc-950 dark:text-gray-100"
            : "z-[9200] flex flex-col overflow-hidden rounded-2xl border border-teal-200 bg-white text-gray-900 shadow-2xl dark:border-teal-900 dark:bg-zinc-950 dark:text-gray-100"
      }
      style={
        contracted && contractedPlacement
          ? contractedPlacement
          : mobileSize
      }
    >
      {resizeEnabled ? <MessengerDragExpandHandle handleProps={handleProps} /> : null}
      <div className="flex shrink-0 items-center gap-2 border-b border-teal-100 px-3 py-2 dark:border-teal-900">
        <p className="text-sm font-semibold text-teal-900 dark:text-teal-100">Messenger</p>
        {isMobile ? (
          <button
            type="button"
            onClick={returnToFireflyMessenger}
            aria-label="Back to Firefly"
            className="inline-flex h-8 items-center justify-center rounded-md px-2 text-[11px] font-medium text-teal-800 hover:bg-teal-50 dark:text-teal-200 dark:hover:bg-teal-950/50"
          >
            Firefly
          </button>
        ) : (
          <button
            type="button"
            onClick={() => {
              if (sizeMode === "expanded") {
                returnToFireflyMessenger();
                return;
              }
              setMessengerSizeMode("expanded");
            }}
            aria-label={
              sizeMode === "expanded" ? "Return to Firefly messenger" : "Expand messenger"
            }
            className="inline-flex h-8 items-center justify-center rounded-md px-2 text-[11px] font-medium text-teal-800 hover:bg-teal-50 dark:text-teal-200 dark:hover:bg-teal-950/50"
          >
            {sizeMode === "expanded" ? "Contract" : "Expand"}
          </button>
        )}
        <button
          type="button"
          onClick={closeMessenger}
          aria-label="Close messenger"
          className="ml-auto inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
        >
          ✕
        </button>
      </div>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden px-3 py-2">
        <WorkplaceMessengerBody active={open} layout={layout} />
      </div>
    </div>
  );

  if (isMobile) {
    return createPortal(
      <div className="fixed inset-0 z-[9200] flex flex-col bg-white dark:bg-zinc-950">{panel}</div>,
      document.body,
    );
  }

  if (expandedDesktop) {
    return createPortal(
      <div className="fixed inset-0 z-[9200] flex items-center justify-center bg-black/40 p-4">
        <button
          type="button"
          aria-label="Dismiss messenger backdrop"
          className="absolute inset-0 cursor-default"
          onClick={closeMessenger}
        />
        <div className="relative z-[1] flex h-full max-h-full w-full max-w-full items-center justify-center">
          {panel}
        </div>
      </div>,
      document.body,
    );
  }

  return createPortal(panel, document.body);
}
